import { darken, lighten } from '@mantine/core'

import type { BoggleBadgeCategory, BoggleBadgeLevel } from './types'

/**
 * Authored category display config. Categories own color in the temporary
 * badge medallion grammar (level/rarity owns shape; see `BadgeMedallion`).
 *
 * The medallion uses this color for the icon; strokes / hex outer rim use a
 * dimmed variant (`frameBorder`). The fill background is derived from the
 * same hue, visibly darker. Keep colors sensibly far apart
 * so categories read distinctly at small medallion sizes.
 *
 * Palette is intentionally **muted** (lower saturation) so badges do not read
 * as neon against the Compendium surface.
 */
export type BoggleBadgeCategoryDisplay = {
  /** Player-visible category name (Title Case). */
  label: string
  /** Base hue for medallion icons; rims use a darker variant of this. */
  color: string
}

export const BOGGLE_BADGE_CATEGORY_DISPLAY: Record<
  BoggleBadgeCategory,
  BoggleBadgeCategoryDisplay
> = {
  length: { color: '#6d8f62', label: 'Length' },
  letters: { color: '#8a6fa8', label: 'Letters' },
  mastery: { color: '#a65f5f', label: 'Mastery' },
  path: { color: '#5f82a8', label: 'Path' },
  special: { color: '#a68b45', label: 'Special' },
  speed: { color: '#a67d4f', label: 'Speed' },
}

export function getBoggleBadgeCategoryDisplay(category: BoggleBadgeCategory) {
  return BOGGLE_BADGE_CATEGORY_DISPLAY[category]
}

/** WoW item-quality text colors for badge level labels in the popover. */
export type BoggleBadgeLevelDisplay = {
  /** Player-visible level name (Title Case). */
  label: string
  color: string
}

export const BOGGLE_BADGE_LEVEL_DISPLAY: Record<
  BoggleBadgeLevel,
  BoggleBadgeLevelDisplay
> = {
  common: { color: '#ffffff', label: 'Common' },
  impossible: { color: '#ff4040', label: 'Impossible' },
  legendary: { color: '#ff8000', label: 'Legendary' },
  rare: { color: '#0070dd', label: 'Rare' },
  uncommon: { color: '#1eff00', label: 'Uncommon' },
}

export function getBoggleBadgeLevelDisplay(level: BoggleBadgeLevel) {
  return BOGGLE_BADGE_LEVEL_DISPLAY[level]
}

/** Stable 0..2 slot from badge id for slight per-badge hue shift within a category. */
function badgeTintSlot(badgeId: string): 0 | 1 | 2 {
  let hash = 0
  for (let index = 0; index < badgeId.length; index += 1) {
    hash = (hash + badgeId.charCodeAt(index) * (index + 3)) % 1009
  }
  return (hash % 3) as 0 | 1 | 2
}

/**
 * Frame + fill colors for a medallion: muted category base with a small
 * per-badge drift so two badges in the same category are not identical.
 *
 * `frameBorder` is a dimmed sibling of `frame` for strokes / legendary outer
 * shell so the rim reads softer while the icon keeps full `frame` chroma.
 */
export function getBoggleBadgeMedallionColors(options: {
  badgeId: string
  category: BoggleBadgeCategory
}): { background: string; frame: string; frameBorder: string } {
  const base = BOGGLE_BADGE_CATEGORY_DISPLAY[options.category].color
  const slot = badgeTintSlot(options.badgeId)
  const frame =
    slot === 0 ? base : slot === 1 ? lighten(base, 0.06) : darken(base, 0.07)
  const background = darken(frame, 0.58)
  const frameBorder = darken(frame, 0.3)
  return { background, frame, frameBorder }
}
